import React, { useState, useRef, useCallback, useEffect } from "react";
import MonacoEditor, { type Monaco } from "@monaco-editor/react";
import { usePermissions } from "../../PermissionsProvider";
import { WorkflowService } from "../../services/WorkflowService";
import GeneralService from "../../services/GeneralService";
import { Button } from "../ui/button";
import {
  CheckCircle2Icon,
  XCircleIcon,
  Loader2Icon,
  AlertTriangleIcon,
  UploadIcon,
} from "lucide-react";

interface WorkflowScriptEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minHeight?: number;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

const HIDDEN_IMPORT = "from maitai_workflow import WorkflowContext";

let completionProviderRegistered = false;
let availableModels: string[] = [];

function setAvailableModels(models: string[]) {
  availableModels = models;
}

function registerWorkflowCompletions(monaco: Monaco) {
  if (completionProviderRegistered) return;
  completionProviderRegistered = true;

  const snippets = [
    {
      label: "ctx.chat_completion",
      detail: "Call an LLM through Maitai",
      insertText: [
        "ctx.chat_completion(",
        "    model=${1:},",
        "    intent=\"${2:intent_name}\",",
        "    messages=[",
        "        {\"role\": \"system\", \"content\": \"${3:You are a helpful assistant.}\"},",
        "        {\"role\": \"user\", \"content\": ${4:user_content}},",
        "    ],",
        ")",
      ].join("\n"),
    },
    {
      label: "ctx.agent_completion",
      detail: "Call a Maitai agent",
      insertText: [
        "ctx.agent_completion(",
        "    agent=\"${1:agent-ref-name}\",",
        "    messages=[",
        "        {\"role\": \"user\", \"content\": ${2:user_content}},",
        "    ],",
        ")",
      ].join("\n"),
    },
    {
      label: "ctx.workflow_completion",
      detail: "Call another workflow",
      insertText: [
        "ctx.workflow_completion(",
        "    workflow=\"${1:workflow-ref-name}\",",
        "    messages=[",
        "        {\"role\": \"user\", \"content\": ${2:user_content}},",
        "    ],",
        ")",
      ].join("\n"),
    },
    {
      label: "ctx.log",
      detail: "Structured logging",
      insertText: "ctx.log(\"${1:message}\", ${2:key}=${3:value})",
    },
    {
      label: "ctx.profile_span",
      detail: "Record a timing span",
      insertText: "ctx.profile_span(\"${1:step_name}\")",
    },
    {
      label: "ctx.emit",
      detail: "Stream a chunk back to the caller",
      insertText: "ctx.emit(\"${1:chunk}\")",
    },
    {
      label: "ctx.input",
      detail: "Raw input data from the request",
      insertText: "ctx.input",
    },
    {
      label: "ctx.messages",
      detail: "Chat messages from the request",
      insertText: "ctx.messages",
    },
    {
      label: "ctx.session_id",
      detail: "Unique session identifier",
      insertText: "ctx.session_id",
    },
  ];

  const allTriggers = "abcdefghijklmnopqrstuvwxyz:._-0123456789=\"'".split("");
  monaco.languages.registerCompletionItemProvider("python", {
    triggerCharacters: [".", ...allTriggers],
    provideCompletionItems: (model, position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const ctxMatch = textUntilPosition.match(/ctx\.(\w*)$/);
      if (ctxMatch) {
        const typed = ctxMatch[1].toLowerCase();
        const ctxDotCol = position.column - typed.length;
        return {
          suggestions: snippets
            .filter((s) => {
              const after = s.label.replace("ctx.", "").toLowerCase();
              return after.startsWith(typed);
            })
            .map((s) => ({
              label: s.label,
              kind: s.insertText.includes("\n")
                ? monaco.languages.CompletionItemKind.Snippet
                : monaco.languages.CompletionItemKind.Method,
              insertText: s.insertText.replace(/^ctx\./, ""),
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              detail: s.detail,
              range: {
                startLineNumber: position.lineNumber,
                startColumn: ctxDotCol,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
              },
            })),
        };
      }

      const modelQuotedMatch = textUntilPosition.match(/model\s*=\s*["']([^"']*)$/);
      const modelBareMatch = !modelQuotedMatch && textUntilPosition.match(/model\s*=\s*(\w*)$/);

      if ((modelQuotedMatch || modelBareMatch) && availableModels.length > 0) {
        const isQuoted = !!modelQuotedMatch;
        const typed = (isQuoted ? modelQuotedMatch![1] : modelBareMatch![1]);
        const startCol = position.column - typed.length;
        return {
          incomplete: true,
          suggestions: availableModels.map((m) => ({
            label: m,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: isQuoted ? m : `"${m}"`,
            filterText: isQuoted ? m : `"${m}"`,
            sortText: m,
            detail: "Available model",
            range: {
              startLineNumber: position.lineNumber,
              startColumn: startCol,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
          })),
        };
      }

      return { suggestions: [] };
    },
  });
}

const stripImport = (code: string): string => {
  const lines = code.split("\n");
  const filtered = lines.filter((line) => line.trim() !== HIDDEN_IMPORT);
  return filtered.join("\n").replace(/^\n+/, "");
};

const prependImport = (editorCode: string): string =>
  `${HIDDEN_IMPORT}\n\n\n${editorCode}`;

const WorkflowScriptEditor: React.FC<WorkflowScriptEditorProps> = ({
  value,
  onChange,
  disabled = false,
  minHeight = 500,
}) => {
  const { apiKey } = usePermissions();
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const editorValue = stripImport(value);

  useEffect(() => {
    if (!apiKey) return;
    const sub = GeneralService.get("/model/", () => {}, JSON, GeneralService.PROD_ENDPOINT, apiKey).subscribe(
      (models: any[]) => {
        const refs = (Array.isArray(models) ? models : [])
          .map((m: any) => m.model_ref)
          .filter(Boolean);
        setAvailableModels(refs);
      },
      () => {}
    );
    return () => sub.unsubscribe();
  }, [apiKey]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const getFullCode = useCallback(
    (editorCode: string) => prependImport(editorCode),
    []
  );

  const handleValidate = useCallback(() => {
    const full = getFullCode(editorValue);
    if (!full.trim()) return;
    setValidating(true);
    setValidation(null);
    WorkflowService.validateWorkflowScript(full, () => {}, apiKey).subscribe(
      (result: ValidationResult) => {
        setValidation(result);
        setValidating(false);
      },
      (err: Error) => {
        setValidation({
          valid: false,
          errors: [`Validation request failed: ${err.message}`],
          warnings: [],
        });
        setValidating(false);
      }
    );
  }, [editorValue, apiKey, getFullCode]);

  const handleEditorChange = (newEditorValue: string | undefined) => {
    const val = newEditorValue || "";
    const full = getFullCode(val);
    onChange(full);
    setValidation(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim()) {
        setValidating(true);
        WorkflowService.validateWorkflowScript(
          full,
          () => {},
          apiKey
        ).subscribe(
          (result: ValidationResult) => {
            setValidation(result);
            setValidating(false);
          },
          () => setValidating(false)
        );
      }
    }, 1500);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        onChange(content.includes(HIDDEN_IMPORT) ? content : prependImport(content));
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-gc-med text-foreground">
            Workflow Script
          </label>
          <span className="text-xs text-muted-foreground">
            Define your workflow logic in the execute(ctx) function
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".py"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="text-xs gap-1"
          >
            <UploadIcon className="w-3 h-3" />
            Upload .py
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleValidate}
            disabled={disabled || validating || !editorValue.trim()}
            className="text-xs gap-1"
          >
            {validating ? (
              <Loader2Icon className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCircle2Icon className="w-3 h-3" />
            )}
            Validate
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <MonacoEditor
          height={minHeight}
          language="python"
          theme="vs-dark"
          value={editorValue}
          onChange={handleEditorChange}
          beforeMount={registerWorkflowCompletions}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            tabSize: 4,
            insertSpaces: true,
            automaticLayout: true,
            readOnly: disabled,
            padding: { top: 16, bottom: 16 },
            quickSuggestions: { other: true, strings: true, comments: false },
            suggestOnTriggerCharacters: true,
          }}
        />
      </div>

      {validating && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2Icon className="w-3 h-3 animate-spin" />
          Validating...
        </div>
      )}

      {validation && !validating && (
        <div className="space-y-2">
          {validation.valid ? (
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2Icon className="w-4 h-4" />
              Script is valid
            </div>
          ) : (
            <div className="space-y-1">
              {validation.errors.map((err, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400"
                >
                  <XCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="font-mono">{err}</span>
                </div>
              ))}
            </div>
          )}
          {validation.warnings.map((warn, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400"
            >
              <AlertTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{warn}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowScriptEditor;
